import React, { useState, useEffect } from "react";
import {
  AddIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Header,
  Text,
  Tooltip,
  TrashCanIcon,
  InfoIcon,
  ScreenshareIcon,
  Dialog,
  CloseIcon,
  FilesTextColoredIcon,
  FilesTxtIcon,
  CalendarAgendaIcon,
  ChevronEndIcon,
  Loader,
} from "@fluentui/react-northstar";
import { Col, Grid, Input, Row, Select } from "antd";
import "./RubricCreator.scss";
import axios from "axios";
import { useAssignment } from "../../../../context/AssignmentContext";
import { useTranslation } from "react-i18next";
import { FileTextOutlined } from "@ant-design/icons";
import { NotificationAlert } from "../../../../components/Notification/Notification";
import { newRubricData } from "./RubricConstant";
import { useMagicBar } from "../../../../context/MagicBarContext";
import { gptIcon } from "../../../../Assets/icons/icons";
import { toast } from "react-toastify";
import { getUserLanguage } from "../../../../utils/i18n";
import i18n from "../../../../helper/i18n";

const { useBreakpoint } = Grid;

const levelPresets = {
  3: [
    { label: "Excellent", color: "level-green" },
    { label: "Average", color: "level-yellow" },
    { label: "Needs Work", color: "level-red" },
  ],
  4: [
    { label: "Excellent", color: "level-green" },
    { label: "Above Average", color: "level-blue" },
    { label: "Below Average", color: "level-yellow" },
    { label: "Needs Work", color: "level-red" },
  ],
  5: [
    { label: "Excellent", color: "level-green" },
    { label: "Above Average", color: "level-blue" },
    { label: "Average", color: "level-indigo" },
    { label: "Below Average", color: "level-yellow" },
    { label: "Needs Work", color: "level-red" },
  ],
  6: [
    { label: "Outstanding", color: "level-emerald" },
    { label: "Excellent", color: "level-green" },
    { label: "Above Average", color: "level-blue" },
    { label: "Average", color: "level-indigo" },
    { label: "Below Average", color: "level-yellow" },
    { label: "Needs Work", color: "level-red" },
  ],
};

const generatePerformanceLevels = (rubricData, levelCount) => {
  return rubricData.map((data) => {
    let currentLevels = data.levels;
    let currentLevelsLength = data.levels?.length;
    if (levelCount > currentLevelsLength) {
      for (let i = 0; i < levelCount - currentLevelsLength; i++) {
        currentLevels.push({
          id: currentLevels[currentLevels?.length - 1]?.id + 1,
          levelTitle: "",
          description: "",
          element: "child",
        });
      }
    } else if (levelCount < currentLevelsLength) {
      currentLevels.splice(
        currentLevelsLength - (currentLevelsLength - levelCount),
        currentLevelsLength - levelCount,
      );
    }
    return {
      ...data,
      levels: [
        ...currentLevels.map((item) => ({
          ...item,
          levelTitle: item.levelTitle,
        })),
      ],
    };
  });
};

const url = process.env.REACT_APP_EP_URL;

const { TextArea } = Input;

const RubricCreator = (props) => {
  const { t } = useTranslation();
  const {
    currentAssignmentData,
    setCurrentAssignmentData,
    setCurrentPage,
    selectedLesson,
    toggleFilter,
  } = useAssignment();

  const { isGeneratingRubrics, generateRubric } = useMagicBar();

  const translation = t("assignment").body;
  const rubricTranslation = translation.rubricPage;
  const [rubricData, setRubricData] = useState([]);

  const [deleteRubricItem, setDeleteRubricItem] = useState({
    id: null,
    isOpen: false,
  });

  const [rubricBankData, setRubricBankData] = useState([]);
  const [subjectRubrics, setSubjectRubrics] = useState([]);
  const [loading, setLoading] = useState({
    isRubricBankLoading: false,
    isMyRubricLoading: false,
  });
  const [rubricBank, setRubricBank] = useState(
    rubricTranslation.dropdownPlaceholder,
  );
  const [selectedSubRubric, setSelectedSubRubric] = useState(
    rubricTranslation.dropdownPlaceholder,
  );
  const [totalLevels, setTotalLevels] = useState(4);
  const [havePoints, setHavePoints] = useState(true);
  const [rubricTitle, setRubricTitle] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(
    currentAssignmentData.status === "Published" &&
      currentAssignmentData?.rubric?.rubricItems?.length,
  );

  const screens = useBreakpoint();

  useEffect(() => {
    setRubricData(
      currentAssignmentData?.rubric?.rubricItems?.length
        ? currentAssignmentData.rubric.rubricItems.map((item) => ({
            ...item,
            levels: item.levels,
          }))
        : !currentAssignmentData?.rubric?.isVisited
          ? newRubricData.map((item) => ({
              ...item,
              havePoints: !!havePoints,
              levels: item.levels.map((criteria) => ({
                ...criteria,
                points: 0,
                havePoints: !!havePoints,
              })),
            }))
          : [],
    );
    currentAssignmentData?.rubric?.rubricItems?.length &&
      setTotalLevels(
        currentAssignmentData?.rubric?.rubricItems?.[0]?.levels?.length || 4,
      );
    setRubricTitle(currentAssignmentData?.rubric?.rubricTitle ?? "");
  }, [props.selectedTab]);

  const handleTotalLevelsChange = (value) => {
    const levelCount = Number(value);
    setTotalLevels(levelCount);
    setRubricData(generatePerformanceLevels(rubricData, levelCount));
  };

  // Responsive grid columns for levels
  const getGridCols = (levelCount) => {
    if (levelCount === 3) return 3;
    if (levelCount === 4) return 4;
    if (levelCount === 5) return 5;
    if (levelCount === 6) return 6;
    return 4;
  };

  const fetchRubricBankData = async (className) => {
    setLoading((prev) => ({ ...prev, isRubricBankLoading: true }));
    try {
      let response = await axios.get(
        `${url}/api/rubric/get-rubric-bank-item${props.user.slug}${
          className ? `&className=${className}` : ""
        }`,
        {
          headers: {
            Authorization: "Bearer " + props.user.accessToken,
          },
        },
      );
      if (response.data.results.length) {
        setRubricBankData([...response.data.results]);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading((prev) => ({ ...prev, isRubricBankLoading: false }));
    }
  };

  const fetchMySubjectRubrics = async (className) => {
    setLoading((prev) => ({ ...prev, isMyRubricLoading: true }));
    try {
      let response = await axios.get(
        `${url}/api/rubric/my-subject-rubrics${props.user.slug}${
          className ? `&className=${className}` : ""
        }`,
        {
          headers: {
            Authorization: "Bearer " + props.user.accessToken,
          },
        },
      );
      if (response.data.results.length) {
        setSubjectRubrics([...response.data.results]);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading((prev) => ({ ...prev, isMyRubricLoading: false }));
    }
  };

  const getLargestId = (data) => {
    let largestId = 1;
    data.forEach((item) => {
      if (item.id > largestId) {
        largestId = item.id;
      }
    });
    return largestId;
  };

  const onValueChange = ({ parentId, id, element, field, value }) => {
    const updatedRubrics = rubricData.map((item) => {
      if (element?.toLowerCase() === "parent") {
        if (item.id === id)
          return {
            ...item,
            [field]: value,
            havePoints: !!havePoints,
            isChanged: true,
          };
        else return { ...item, havePoints: !!havePoints };
      } else if (
        element?.toLowerCase() === "child" &&
        item.levels?.length &&
        parentId === item.id
      ) {
        return {
          ...item,
          levels: item.levels.map((data) => {
            if (data.id === id) {
              return { ...data, [field]: value, isChanged: true };
            } else {
              return data;
            }
          }),
        };
      } else return item;
    });
    setRubricData(updatedRubrics);
    if (field === "points") {
      let totalPoints = updatedRubrics
        .map((item) => {
          return Math.max(...item.levels.map((e) => e.points));
        })
        .reduce((sum, val) => sum + val, 0);
      setCurrentAssignmentData((prevData) => ({
        ...prevData,
        markingType: totalPoints,
        isChanged: true,
      }));
    }

    // 2️⃣ Persist immediately (THIS prevents data loss on tab switch)
    setCurrentAssignmentData((prev) => ({
      ...prev,
      isChanged: true,
      rubric: {
        ...prev.rubric,
        rubricTitle,
        rubricItems: updatedRubrics,
        isVisited: true,
      },
    }));
  };

  const duplicateBtnHandler = ({ id, index }) => {
    rubricData.splice(index + 1, 0, {
      ...rubricData[index],
      id: getLargestId(rubricData) + 1,
    });
    setRubricData([...rubricData]);
  };

  const deleteBtnHandler = ({ id }) => {
    setRubricData((prevRubricData) =>
      prevRubricData.filter((item) => item.id !== id),
    );
    setCurrentAssignmentData((prevData) => ({
      ...prevData,
      isChanged: true,
      rubric: {
        ...prevData.rubric,
        isRubricItemDeleted: true,
      },
    }));
  };

  const addMoreCriteriaBtnHandler = () => {
    let levels = [];
    for (let i = 0; i < totalLevels; i++) {
      levels.push({
        id: i + 1,
        levelTitle: "",
        description: "",
        element: "child",
        points: 0,
      });
    }
    setRubricData((prevRubricData) => [
      ...prevRubricData,
      {
        id: getLargestId(rubricData) + 1,
        title: "",
        description: "",
        element: "parent",
        levels: levels,
        havePoints: !!havePoints,
      },
    ]);
  };

  // Validation function
  const validateRubric = () => {
    const errors = {};
    // Title validation
    if (!rubricTitle || rubricTitle.trim().length === 0) {
      errors.title = "Title is required.";
    } else if (rubricTitle.trim().length < 3) {
      errors.title = "Title must be at least 3 characters.";
    }
    // Description validation (for each criterion)
    rubricData.forEach((criterion, idx) => {
      if (!criterion.title || criterion.title.trim().length === 0) {
        errors[`criteria-title-${criterion.id}`] = "Criteria name is required.";
      }
      if (!criterion.description || criterion.description.trim().length === 0) {
        errors[`criteria-description-${criterion.id}`] =
          "Description is required.";
      }
      // Levels validation
      if (!criterion.levels || criterion.levels.length < 2) {
        errors[`criteria-levels-${criterion.id}`] =
          "At least two levels are required.";
      }
      criterion.levels?.forEach((level, lidx) => {
        if (!level.levelTitle || level.levelTitle.trim().length === 0) {
          errors[`level-title-${criterion.id}-${level.id}`] =
            "Level name is required.";
        }
        if (havePoints) {
          if (
            level.points === undefined ||
            level.points === null ||
            level.points === "" ||
            isNaN(Number(level.points)) ||
            Number(level.points) < 0
          ) {
            errors[`level-points-${criterion.id}-${level.id}`] =
              "Points must be a number ≥ 0.";
          }
        }
      });
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextButtonHandler = () => {
    const isValid = validateRubric();
    if (!isValid) return;
    toggleFilter();
    if (havePoints || +String(currentAssignmentData.markingType) > 0) {
      let points = 0;
      rubricData.forEach((rubricItem) => {
        points += Math.max(
          ...rubricItem.levels.map((l) => Number(l.points) || 0),
        );
      });

      if (!(points == +String(currentAssignmentData.markingType))) {
        NotificationAlert(
          `${rubricTranslation.rubricError} (${currentAssignmentData.markingType})`,
          "error",
        );
        return;
      } else {
        setCurrentAssignmentData((prevData) => ({
          ...prevData,
          isChanged: true,
          rubric: {
            ...prevData.rubric,
            rubricTitle,
            rubricItems: rubricData.map((item) => {
              if (!havePoints) {
                item.havePoints = false;
                item.levels?.forEach((childItem) => {
                  delete childItem.points;
                });
                return item;
              } else {
                return item;
              }
            }),
            isVisited: true,
          },
        }));
        // NotificationAlert(rubricTranslation.rubricSaved, "success");
        // props.history.push("/assignments/create");
        props.setState({ isCreatingRubric: false });
      }
    } else {
      setCurrentAssignmentData((prevData) => ({
        ...prevData,
        isChanged: true,
        rubric: {
          ...prevData.rubric,
          rubricTitle,
          rubricItems: rubricData.map((item) => {
            if (!havePoints) {
              item.havePoints = false;
              item.levels?.forEach((childItem) => {
                delete childItem.points;
              });
              return item;
            } else {
              return item;
            }
          }),
          isVisited: true,
        },
      }));
      // NotificationAlert(rubricTranslation.rubricSaved, "success");
      // props.history.push("/assignments/create");
      props.setState({ isCreatingRubric: false });
    }
    props.setSelectedTab("settings");
  };

  useEffect(() => {
    let className = "";
    if (selectedLesson?.ClassName) {
      className = selectedLesson.ClassName.substring(
        0,
        selectedLesson.ClassName.lastIndexOf("-"),
      ).trim();
    }
    if (props) {
      fetchRubricBankData(className);
      fetchMySubjectRubrics(className);
    }
  }, []);

  const handleGenerateRubricsWithAi = async () => {
    if (
      !currentAssignmentData.assignmentTitle ||
      !currentAssignmentData.instruction
    ) {
      toast.error(
        "Please enter both an assignment title and instructions before generating rubrics.",
      );
      return;
    }
    const payload = {
      assignmentTitle: currentAssignmentData.assignmentTitle,
      assignmentDescription: "",
      assignmentInstructions: currentAssignmentData.instruction,
      language: getUserLanguage(i18n.language),
      hasPoints: havePoints,
    };
    const rubric = await generateRubric(payload);


    if (rubric) {
      setRubricTitle(rubric.rubricTitle);
      let totalPoints = 0;
      rubric.rubricItem.forEach((item) => {
        totalPoints += Math.max(
          ...item.rubricCriteria.map((e) => Number(e.points) || 0),
        );
      });
      const formattedRubricData = rubric.rubricItem.map((item) => ({
        id: item.rubricItemId,
        title: item.rubricItemTitle,
        description: item.description,
        element: item.element,
        havePoints: !!havePoints,
        levels: item.rubricCriteria.map((e) => ({
          id: e.criteriaId,
          levelTitle: e.criteriaTitle,
          description: e.description,
          element: "Child",
          havePoints: !!havePoints,
          points: e.points,
        })),
      }));

      // ✅ Persist AI-generated rubric in assignment context
      setCurrentAssignmentData((prev) => ({
        ...prev,
        isChanged: true,
        markingType: totalPoints,
        rubric: {
          ...prev.rubric,
          rubricTitle: rubric.rubricTitle,
          // rubricTitle: rubric.rubricTitle,
          rubricItems: formattedRubricData,
          isVisited: true,
        },
      }));

      setTotalLevels(rubric?.rubricItems?.[0]?.rubricCriteria?.length || 4);
      setRubricData(formattedRubricData);
      props.setMarkingType(totalPoints);
      // setCurrentAssignmentData((prevData) => ({
      //   ...prevData,
      //   markingType: totalPoints,
      //   isChanged: true,
      // }));
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {isGeneratingRubrics && (
        <div className="rubric-generating-overlay">
          <Flex fill vAlign="center" hAlign="center">
            <Loader label="Generating Rubrics..." />
          </Flex>
        </div>
      )}
      <Box
        className="rubric-creator-wrapper rubric-creator-bg"
        style={{ overflowY: isGeneratingRubrics ? "hidden" : "auto" }}
      >
        <Box className="rubric-creator-container">
          <Flex
            hAlign={screens.xs ? "center" : "end"}
            gap="gap.smaller"
            style={{
              marginBottom: 16,
              flexDirection: screens.xs ? "column" : "row",
              width: "100%",
            }}
          >
            {!isPreviewMode && (
              <Button
                data-cy="generate-rubrics-with-ai-btn"
                content={"Generate"}
                icon={gptIcon()}
                onClick={handleGenerateRubricsWithAi}
                disabled={isGeneratingRubrics}
                loading={isGeneratingRubrics}
                style={{
                  borderRadius: 8,
                  width: screens.xs ? "100%" : "auto",
                  maxWidth: screens.xs ? "300px" : "none",
                }}
              />
            )}
            <Button
              data-cy="preview-mode-toggler-btn"
              content={isPreviewMode ? "Edit" : "Preview"}
              onClick={() => setIsPreviewMode((prev) => !prev)}
              style={{
                borderRadius: 8,
                width: screens.xs ? "100%" : "auto",
                maxWidth: screens.xs ? "300px" : "none",
              }}
            />
          </Flex>
          <Header
            as="h2"
            align="center"
            style={{
              marginBottom: 8,
              fontSize: screens.xs ? "20px" : "24px",
              padding: screens.xs ? "0 16px" : "0",
            }}
            className="section-header"
          >
            {currentAssignmentData?.rubric?.rubricItems?.length
              ? rubricTranslation.editRubric
              : rubricTranslation.createRubric}
          </Header>
          <Text
            align="center"
            style={{
              display: "block",
              marginBottom: 24,
              color: "#666",
              fontSize: screens.xs ? "14px" : "16px",
              padding: screens.xs ? "0 16px" : "0",
            }}
          >
            Define criteria and performance levels for your assessment rubric
          </Text>

          {/* Top Controls */}
          {!isPreviewMode && (
            <Box className="rubric-controls-card">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Flex column gap="gap.smaller">
                    <Text weight="semibold">Rubric Bank:</Text>
                    <Select
                      data-cy="rubric-bank-selector"
                      value={rubricBank}
                      style={{ width: "100%" }}
                      options={
                        rubricBankData?.length
                          ? [
                              {
                                value: rubricTranslation.dropdownPlaceholder,
                                label: rubricTranslation.dropdownPlaceholder,
                                disabled: true,
                              },
                              ...rubricBankData?.map((rubric) => ({
                                value: rubric.rubricBankId,
                                label: rubric.rubricTitle,
                              })),
                            ]
                          : [
                              {
                                value: rubricTranslation.noDataAvilable,
                                label: rubricTranslation.noDataAvilable,
                                disabled: true,
                              },
                            ]
                      }
                      placeholder={
                        loading.isRubricBankLoading
                          ? translation.loading
                          : rubricTranslation.dropdownPlaceholder
                      }
                      loading={loading.isRubricBankLoading}
                      onChange={(value) => {
                        setRubricBank(value);
                        let data = rubricBankData.find(
                          (rubric) => rubric.rubricBankId === value,
                        )?.rubricBankItem;

                        setRubricTitle(
                          rubricBankData.find(
                            (rubric) => rubric.rubricBankId === value,
                          )?.rubricTitle,
                        );

                        if (data?.length) {
                          setRubricData(
                            data.map((item) => ({
                              id: item.rubricBankItemId,
                              title: item.rubricTitle,
                              description: item.description,
                              element: item.element,
                              havePoints: !!havePoints,
                              levels: item.rubricBankCriteria.map((e) => ({
                                id: e.rubricBankCriteriaId,
                                levelTitle: e.criteriaTitle,
                                description: e.description,
                                element: "Child",
                                havePoints: !!havePoints,
                                points: e.points,
                              })),
                            })),
                          );
                        } else {
                          setRubricData([]);
                        }
                      }}
                    />
                  </Flex>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Flex column gap="gap.smaller">
                    <Text weight="semibold">My Rubrics:</Text>
                    <Select
                      data-cy="my-rubrics-selector"
                      style={{ width: "100%" }}
                      options={
                        subjectRubrics?.length
                          ? [
                              {
                                value: rubricTranslation.dropdownPlaceholder,
                                label: rubricTranslation.dropdownPlaceholder,
                                disabled: true,
                              },
                              ...subjectRubrics?.map((rubric) => ({
                                value: rubric.rubricId,
                                label: rubric.rubricTitle,
                              })),
                            ]
                          : [
                              {
                                value: rubricTranslation.noDataAvilable,
                                label: rubricTranslation.noDataAvilable,
                                disabled: true,
                              },
                            ]
                      }
                      placeholder={
                        loading.isMyRubricLoading
                          ? translation.loading
                          : rubricTranslation.dropdownPlaceholder
                      }
                      loading={loading.isMyRubricLoading}
                      value={selectedSubRubric}
                      onChange={(value) => {
                        setSelectedSubRubric(value);
                        let data = subjectRubrics.find(
                          (rubric) => rubric.rubricId === value,
                        )?.rubricItem;

                        setRubricTitle(
                          subjectRubrics.find(
                            (rubric) => rubric.rubricId === value,
                          )?.rubricTitle,
                        );

                        let totalPoints = data
                          .map((item) => {
                            return Math.max(
                              ...item.rubricCriteria.map((e) => e.points),
                            );
                          })
                          .reduce((sum, val) => sum + val, 0);

                        if (data?.length) {
                          setCurrentAssignmentData({
                            ...currentAssignmentData,
                            markingType: totalPoints,
                          });
                          setRubricData(
                            data.map((item) => ({
                              id: item.rubricItemId,
                              title: item.rubricItemTitle,
                              description: item.description,
                              element: item.element,
                              havePoints: !!havePoints,
                              levels: item.rubricCriteria.map((e) => ({
                                id: e.criteriaId,
                                levelTitle: e.criteriaTitle,
                                description: e.description,
                                element: "Child",
                                points: e.points,
                                havePoints: !!havePoints,
                              })),
                            })),
                          );
                        } else {
                          setRubricData([]);
                        }
                      }}
                    />
                  </Flex>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Flex column gap="gap.smaller">
                    <Text weight="semibold">Total Levels</Text>
                    <Select
                      data-cy="rubric-levels-selector"
                      value={totalLevels}
                      style={{ width: "100%" }}
                      onChange={(v) => handleTotalLevelsChange(v)}
                      options={[
                        { value: 3, label: "3 Levels" },
                        { value: 4, label: "4 Levels" },
                        { value: 5, label: "5 Levels" },
                        { value: 6, label: "6 Levels" },
                      ]}
                      placeholder="Select levels"
                    />
                  </Flex>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Flex
                    column={screens.xs}
                    gap="gap.smaller"
                    vAlign={screens.xs ? "start" : "center"}
                    style={{ marginTop: screens.xs ? 0 : 32 }}
                  >
                    <Checkbox
                      data-cy="rubric-haspoints-toggler"
                      checked={havePoints}
                      label={<Text>Have Points</Text>}
                      onChange={(e, data) => setHavePoints(data.checked)}
                    />
                  </Flex>
                </Col>
              </Row>
              <Box className="rubric-helper-box">
                <Flex gap="gap.smaller" vAlign="start">
                  <InfoIcon outline />
                  <Box>
                    <Text weight="semibold">Dynamic Performance Levels</Text>
                    <Text style={{ display: "block" }}>
                      Performance level cards will automatically update based on
                      your "Total Levels" selection. Any existing data will be
                      preserved when possible.
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Box>
          )}

          <Box className="rubric-title-card" style={{ position: "relative" }}>
            <Flex column gap="gap.smaller">
              {havePoints && (
                <Flex hAlign="end">
                  <div
                    style={{
                      width: "fit-content",
                      backgroundColor: "#eae6ff",
                      borderRadius: "20px",
                      padding: screens.xs ? "4px 8px" : "5px 10px",
                      color: "#6264a7",
                      height: "fit-content",
                      fontSize: screens.xs ? "12px" : "14px",
                    }}
                  >
                    <Flex gap="gap.smaller" vAlign="center">
                      <Text
                        weight="semibold"
                        content="Total points :"
                        style={{ fontSize: screens.xs ? "12px" : "14px" }}
                      />
                      <Text
                        weight="bold"
                        content={currentAssignmentData.markingType || 0}
                        style={{ fontSize: screens.xs ? "12px" : "14px" }}
                      />
                    </Flex>
                  </div>
                </Flex>
              )}
              <Row justify="center">
                <Col xs={24} md={20} lg={16}>
                  <Flex column gap="gap.smaller" hAlign="center">
                    <Flex gap="gap.smaller" vAlign="center" hAlign="center">
                      <Box
                        className="rubric-title-icon"
                        style={{
                          background: "#eae6ff",
                          width: screens.xs ? 24 : 30,
                          height: screens.xs ? 24 : 30,
                          borderRadius: 8,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CalendarAgendaIcon
                          size={screens.xs ? "medium" : "large"}
                          style={{ color: "#6264a7" }}
                        />
                      </Box>
                      <label
                        htmlFor="rubric-title"
                        className="section-header"
                        style={{
                          fontWeight: 600,
                          fontSize: screens.xs ? 16 : 18,
                          marginBottom: 0,
                        }}
                      >
                        {rubricTranslation.rubricBankTitleHeader}
                      </label>
                    </Flex>
                    <Box style={{ width: "100%" }}>
                      <Input
                        data-cy="rubric-title-input"
                        id="rubric-title"
                        value={rubricTitle}
                        onChange={(e) => {
                          setRubricTitle(e.target.value);
                          setValidationErrors((prev) => ({
                            ...prev,
                            title: undefined,
                          }));
                          setCurrentAssignmentData((prev) => ({
                            ...prev,
                            isChanged: true,
                            rubric: {
                              ...prev.rubric,
                              rubricTitle: e.target.value,
                              // rubricItems: rubricData,
                              isVisited: true,
                            },
                          }));
                        }}
                        placeholder={
                          "Enter a title for your rubric (e.g., 'Essay Writing Assessment', 'Science Project Evaluation')"
                        }
                        style={{
                          textAlign: "center",
                          fontSize: screens.xs ? 16 : 20,
                          fontWeight: 500,
                          height: screens.xs ? 48 : 56,
                          background: "#f9fafb",
                          border: "2px solid #e5e7eb",
                          borderRadius: 8,
                          marginTop: 8,
                          marginBottom: 0,
                          width: "100%",
                        }}
                        readOnly={isPreviewMode}
                        aria-label="Rubric Title"
                      />
                      {validationErrors.title && !isPreviewMode && (
                        <Text
                          style={{
                            color: "#d13438",
                            fontSize: screens.xs ? 12 : 14,
                            marginTop: 4,
                            textAlign: "center",
                            display: "block",
                          }}
                        >
                          {validationErrors.title}
                        </Text>
                      )}
                      {rubricTitle && !isPreviewMode && (
                        <Box
                          style={{
                            marginTop: 16,
                            padding: screens.xs ? 12 : 16,
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            borderRadius: 8,
                            width: "100%",
                          }}
                        >
                          <Text
                            align="center"
                            style={{
                              display: "block",
                              fontSize: screens.xs ? 16 : 18,
                              fontWeight: 600,
                              color: "#1e40af",
                            }}
                          >
                            "{rubricTitle}"
                          </Text>
                          <Text
                            align="center"
                            style={{
                              display: "block",
                              fontSize: screens.xs ? 12 : 14,
                              color: "#2563eb",
                              marginTop: 4,
                            }}
                          >
                            This title will appear at the top of your completed
                            rubric
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Flex>
                </Col>
              </Row>
            </Flex>
          </Box>

          {/* Criteria Sections */}
          <Box>
            {rubricData.map((critearia, index) => (
              <Box
                key={critearia.id}
                className="rubric-critearia-card fade-in"
                data-cy="rubric-critearea"
              >
                <Row justify="space-between" align="middle">
                  <Col xs={24} sm={18} md={20}>
                    <Header
                      as="h3"
                      style={{
                        margin: 0,
                        fontSize: screens.xs ? 16 : 18,
                        wordBreak: "break-word",
                      }}
                    >
                      #{index + 1} {critearia.title || "Criteria"}
                    </Header>
                  </Col>
                  <Col xs={24} sm={6} md={4}>
                    {!isPreviewMode && (
                      <Flex
                        gap="gap.small"
                        hAlign={screens.xs ? "start" : "end"}
                        style={{ marginTop: screens.xs ? 8 : 0 }}
                      >
                        <Tooltip content={rubricTranslation.duplicate}>
                          <Button
                            data-cy="duplicate-critearea-btn"
                            icon={<ScreenshareIcon />}
                            text
                            onClick={() =>
                              duplicateBtnHandler({ id: critearia.id, index })
                            }
                            size="small"
                            title={rubricTranslation.duplicate}
                            iconOnly
                          />
                        </Tooltip>
                        <Tooltip content={translation.delete}>
                          <Button
                            data-cy="delete-critearea-btn"
                            icon={<TrashCanIcon />}
                            text
                            onClick={() =>
                              setDeleteRubricItem({
                                id: critearia.id,
                                isOpen: true,
                              })
                            }
                            size="small"
                            title={translation.delete}
                            disabled={rubricData.length === 1}
                            styles={{
                              color:
                                rubricData.length === 1 ? undefined : "#d13438",
                              ":hover": {
                                color: "#d13438",
                              },
                            }}
                            iconOnly
                          />
                        </Tooltip>
                      </Flex>
                    )}
                  </Col>
                </Row>
                <Divider style={{ margin: "10px 0" }} />
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={24} lg={10}>
                    <Flex column gap="gap.smaller">
                      <Text
                        weight="semibold"
                        style={{ fontSize: screens.xs ? 14 : 16 }}
                      >
                        {rubricTranslation.criteriaName}
                      </Text>
                      {isPreviewMode ? (
                        <Box
                          style={{
                            minHeight: screens.xs ? 36 : 38,
                            background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: screens.xs ? "6px 10px" : "8px 12px",
                            fontSize: screens.xs ? 14 : 16,
                            color: critearia.title ? undefined : "#aaa",
                          }}
                        >
                          {critearia.title || <span>No name</span>}
                        </Box>
                      ) : (
                        <Input
                          placeholder={rubricTranslation.criteriaName}
                          data-cy="critearia-title-input"
                          value={critearia.title}
                          name="title"
                          onChange={(e) => {
                            onValueChange({
                              id: critearia.id,
                              element: critearia.element,
                              field: "title",
                              value: e.target.value,
                            });
                            setValidationErrors((prev) => ({
                              ...prev,
                              [`criteria-title-${critearia.id}`]: undefined,
                            }));
                          }}
                          fluid
                          disabled={isPreviewMode}
                          style={{
                            fontSize: screens.xs ? 14 : 16,
                            height: screens.xs ? 36 : 38,
                          }}
                        />
                      )}
                      {validationErrors[`criteria-title-${critearia.id}`] &&
                        !isPreviewMode && (
                          <Text
                            style={{
                              color: "#d13438",
                              fontSize: screens.xs ? 12 : 13,
                            }}
                          >
                            {validationErrors[`criteria-title-${critearia.id}`]}
                          </Text>
                        )}
                    </Flex>
                  </Col>
                  <Col xs={24} md={24} lg={14}>
                    <Flex column gap="gap.smaller">
                      <Text
                        weight="semibold"
                        style={{ fontSize: screens.xs ? 14 : 16 }}
                      >
                        {rubricTranslation.description}
                      </Text>
                      {isPreviewMode ? (
                        <Box
                          style={{
                            minHeight: screens.xs ? 36 : 38,
                            background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: screens.xs ? "6px 10px" : "8px 12px",
                            fontSize: screens.xs ? 14 : 16,
                            color: critearia.description ? undefined : "#aaa",
                          }}
                        >
                          {critearia.description || <span>No description</span>}
                        </Box>
                      ) : (
                        <TextArea
                          data-cy="critearia-description-textarea"
                          placeholder={rubricTranslation.description}
                          value={critearia.description}
                          name="description"
                          onChange={(e) => {
                            onValueChange({
                              id: critearia.id,
                              element: critearia.element,
                              field: "description",
                              value: e.target.value,
                            });
                            setValidationErrors((prev) => ({
                              ...prev,
                              [`criteria-description-${critearia.id}`]:
                                undefined,
                            }));
                          }}
                          rows={screens.xs ? 2 : 3}
                          disabled={isPreviewMode}
                          style={{
                            fontSize: screens.xs ? 14 : 16,
                          }}
                        />
                      )}
                      {validationErrors[
                        `criteria-description-${critearia.id}`
                      ] &&
                        !isPreviewMode && (
                          <Text
                            style={{
                              color: "#d13438",
                              fontSize: screens.xs ? 12 : 13,
                            }}
                          >
                            {
                              validationErrors[
                                `criteria-description-${critearia.id}`
                              ]
                            }
                          </Text>
                        )}
                    </Flex>
                  </Col>
                </Row>
                {validationErrors[`criteria-levels-${critearia.id}`] &&
                  !isPreviewMode && (
                    <Text
                      style={{
                        color: "#d13438",
                        fontSize: screens.xs ? 12 : 13,
                        marginLeft: screens.xs ? 4 : 8,
                      }}
                    >
                      {validationErrors[`criteria-levels-${critearia.id}`]}
                    </Text>
                  )}
                <Divider
                  style={{
                    margin: screens.xs ? "16px 0 12px 0" : "24px 0 16px 0",
                  }}
                />
                <Text
                  weight="semibold"
                  style={{ fontSize: screens.xs ? 14 : 16 }}
                >
                  Performance Levels
                </Text>
                <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                  {critearia.levels.map((level, levelIndex) => {
                    const levelInfo = levelPresets[totalLevels][levelIndex];
                    return (
                      <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={
                          totalLevels === 5 ? 4 : 24 / getGridCols(totalLevels)
                        }
                        key={level.id}
                      >
                        <Box
                          className={`rubric-level-card ${levelInfo?.color} fade-in`}
                          style={{ animationDelay: `${levelIndex * 0.1}s` }}
                        >
                          <Flex column gap="gap.small">
                            {isPreviewMode ? (
                              <Box
                                style={{
                                  minHeight: 38,
                                  background: "#f9fafb",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  padding: "8px 12px",
                                  fontSize: 15,
                                  color: level.levelTitle ? undefined : "#aaa",
                                }}
                              >
                                {level.levelTitle || <span>No level name</span>}
                              </Box>
                            ) : (
                              <Input
                                data-cy="critearia-level-title"
                                placeholder={levelInfo?.label}
                                value={level.levelTitle}
                                onChange={(e) => {
                                  onValueChange({
                                    parentId: critearia.id,
                                    id: level.id,
                                    element: level.element,
                                    field: "levelTitle",
                                    value: e.target.value,
                                  });
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    [`level-title-${critearia.id}-${level.id}`]:
                                      undefined,
                                  }));
                                }}
                                disabled={isPreviewMode}
                              />
                            )}
                            {validationErrors[
                              `level-title-${critearia.id}-${level.id}`
                            ] &&
                              !isPreviewMode && (
                                <Text
                                  style={{ color: "#d13438", fontSize: 13 }}
                                >
                                  {
                                    validationErrors[
                                      `level-title-${critearia.id}-${level.id}`
                                    ]
                                  }
                                </Text>
                              )}
                            {havePoints &&
                              (isPreviewMode ? (
                                <Box
                                  style={{
                                    minHeight: 38,
                                    background: "#f9fafb",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    padding: "8px 12px",
                                    fontSize: 15,
                                    color:
                                      level.points !== undefined &&
                                      level.points !== null &&
                                      level.points !== ""
                                        ? undefined
                                        : "#aaa",
                                    marginTop: 4,
                                  }}
                                >
                                  {level.points !== undefined &&
                                  level.points !== null &&
                                  level.points !== "" ? (
                                    level.points
                                  ) : (
                                    <span>No points</span>
                                  )}{" "}
                                  <span
                                    style={{ color: "#888", fontWeight: 400 }}
                                  >
                                    Points
                                  </span>
                                </Box>
                              ) : (
                                <Flex gap="gap.smaller" vAlign="end">
                                  <Input
                                    type="number"
                                    data-cy="critearia-level-points"
                                    placeholder="Points"
                                    value={level.points}
                                    style={{ width: "150px" }}
                                    onChange={(e) => {
                                      onValueChange({
                                        parentId: critearia.id,
                                        id: level.id,
                                        element: level.element,
                                        field: "points",
                                        value: e.target.value,
                                      });
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        [`level-points-${critearia.id}-${level.id}`]:
                                          undefined,
                                      }));
                                    }}
                                    fluid
                                    disabled={isPreviewMode}
                                  />
                                  <Text content="Points" weight="semibold" />
                                </Flex>
                              ))}
                            {havePoints &&
                              validationErrors[
                                `level-points-${critearia.id}-${level.id}`
                              ] &&
                              !isPreviewMode && (
                                <Text
                                  style={{ color: "#d13438", fontSize: 13 }}
                                >
                                  {
                                    validationErrors[
                                      `level-points-${critearia.id}-${level.id}`
                                    ]
                                  }
                                </Text>
                              )}
                            {isPreviewMode ? (
                              <Box
                                style={{
                                  minHeight: 38,
                                  background: "#f9fafb",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  padding: "8px 12px",
                                  fontSize: 15,
                                  color: level.description ? undefined : "#aaa",
                                  marginTop: 4,
                                }}
                              >
                                {level.description || (
                                  <span>No description</span>
                                )}
                              </Box>
                            ) : (
                              <TextArea
                                placeholder={rubricTranslation.description}
                                value={level.description}
                                data-cy="critearia-level-description"
                                onChange={(e) => {
                                  onValueChange({
                                    parentId: critearia.id,
                                    id: level.id,
                                    element: level.element,
                                    field: "description",
                                    value: e.target.value,
                                  });
                                }}
                                rows={3}
                                disabled={isPreviewMode}
                              />
                            )}
                          </Flex>
                        </Box>
                      </Col>
                    );
                  })}
                </Row>
              </Box>
            ))}
          </Box>

          {!isPreviewMode && (
            <Flex hAlign="center" style={{ margin: "32px 0 0 0" }}>
              <Button
                data-cy="add-more-critearea-btn"
                primary
                icon={<AddIcon />}
                content={rubricTranslation.addMoreCriteria}
                onClick={addMoreCriteriaBtnHandler}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  border: "1.5px dashed",
                  padding: "10px",
                  height: "auto",
                  borderRadius: "8px",
                }}
                text
                size={screens.md ? "large" : undefined}
              />
            </Flex>
          )}
        </Box>
        {!isPreviewMode && (
          <div className="botton-action-bar">
            <Flex gap="gap.small" hAlign="end" vAlign="center">
              <Button
                primary
                data-cy="add-more-critearea-btn-footer"
                icon={<AddIcon />}
                content={rubricTranslation.addMoreCriteria}
                onClick={addMoreCriteriaBtnHandler}
                style={{
                  border: "1.5px dashed",
                  borderRadius: "8px",
                }}
                text
              />
              <Button
                data-cy="cancel-rubric-creation"
                content="Cancel"
                onClick={() => {
                  props.setState({ isCreatingRubric: false });
                  setRubricData([]);
                  setRubricTitle("");
                  setSelectedSubRubric("");
                  setRubricBank("");
                  props.setSelectedTab("form");
                }}
                style={{ borderRadius: "8px" }}
              />
              <Button
                data-cy="next-btn"
                primary
                onClick={nextButtonHandler}
                icon={<ChevronEndIcon />}
                iconPosition="after"
                content="Next"
                style={{ borderRadius: "8px" }}
              />
            </Flex>
          </div>
        )}
      </Box>
      <Dialog
        open={deleteRubricItem.isOpen}
        onConfirm={() => {
          deleteBtnHandler({ id: deleteRubricItem.id });
          setDeleteRubricItem({ id: null, isOpen: false });
        }}
        confirmButton={rubricTranslation.deleteConfirm}
        header={rubricTranslation.deleteConfirmation}
        cancelButton={rubricTranslation.deleteCancel}
        onCancel={() => {
          setDeleteRubricItem({ id: null, isOpen: false });
        }}
        className="smallDlg"
        headerAction={{
          icon: <CloseIcon />,
          title: translation.close,
          onClick: () => {
            setDeleteRubricItem({ id: null, isOpen: false });
          },
        }}
      />
    </div>
  );
};

export default RubricCreator;
